import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { IconScout } from '@/components/ui/images/IconScout';
import { GoogleStaticMap } from '@/components/ui/maps/GoogleStaticMap';
import { SidePanelFormLayout } from '@/components/layout';
import { formatRut } from '@/utils';
import type { MarkReport } from '@/api/domains/attendance/types/marksReport.types';


export interface MarkDetailPanelProps {
  record: MarkReport | null;
}

export const MarkDetailPanel: React.FC<MarkDetailPanelProps> = ({ record }) => {
  const { t: translate } = useTranslation();

  const imageUrl = useMemo(() => {
    if (!record) return undefined;
    if (record.photoUrl) return record.photoUrl;
    if (record.photoBase64) {
      if (record.photoBase64.startsWith('data:image/')) return record.photoBase64;
      return `data:image/jpeg;base64,${record.photoBase64}`;
    }
    return undefined;
  }, [record]);

  const hasCoordinates = record?.latitude != null && record?.longitude != null;

  return (
    <SidePanelFormLayout data-testid="mark-detail-panel">
      <div className="w-full flex justify-center">
        <GoogleStaticMap
          mapUrl={record?.mapUrl ?? undefined}
          isUnavailable={!hasCoordinates || !record?.mapUrl}
          loading={false}
          ariaLabel={translate('attendance:marksReport.detail.mapUnavailable')}
        />
      </div>

      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('attendance:marksReport.detail.rut')}
          value={record ? formatRut(record.rut) : ''}
          disabled
        />
        <LabelInput
          label={translate('attendance:marksReport.detail.employee')}
          value={record?.fullName ?? ''}
          disabled
        />
        <LabelInput
          label={translate('attendance:marksReport.detail.date')}
          value={record?.markDate ?? record?.workdayDate ?? ''}
          disabled
          iconRight={<IconScout name="calendarAlt" size="sm" />}
        />
        <LabelInput
          label={translate('attendance:marksReport.detail.time')}
          value={record?.markTime ?? ''}
          disabled
          iconRight={<IconScout name="clockThree" size="sm" />}
        />
        <LabelInput
          label={translate('attendance:marksReport.detail.type')}
          value={record?.markType ?? ''}
          disabled
        />
        <LabelInput
          label={translate('attendance:marksReport.detail.event')}
          value={record?.eventName ?? ''}
          disabled
        />
        <LabelInput
          label={translate('attendance:marksReport.detail.device')}
          value={record?.deviceName ?? ''}
          disabled
        />
        <LabelInput
          label={translate('attendance:marksReport.detail.location')}
          value={record?.locationName ?? ''}
          disabled
        />
        <LabelInput
          label={translate('attendance:marksReport.detail.note')}
          value={record?.note ?? ''}
          disabled
        />
        <LabelInput
          label={translate('attendance:marksReport.detail.observation')}
          value={record?.observation ?? ''}
          disabled
        />
      </SidePanelFormLayout.Fields>

      <div className="w-full flex flex-col items-center gap-2 pt-2">
        <span className="text-sm font-medium self-start">
          {translate('attendance:marksReport.detail.photo')}
        </span>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={translate('attendance:marksReport.detail.photo')}
            className="rounded-lg border max-w-full"
            data-testid="mark-detail-photo"
          />
        ) : (
          <div
            className="w-full flex items-center justify-center rounded-lg border py-8"
            data-testid="mark-detail-photo-unavailable"
          >
            <span className="text-xs text-gray-500">
              {translate('attendance:marksReport.detail.photoUnavailable')}
            </span>
          </div>
        )}
      </div>
    </SidePanelFormLayout>
  );
};

export default MarkDetailPanel;
